import {
	AsyncNotifications,
	DocumentStatusMessageType,
	notify,
	NotifyEvent,
	NotifyListener,
} from '@/services/AsyncNotifications';

export enum Ready {
	now = 'now',
	later = 'later',
}

export interface UrlReadyNow {
	ready: Ready.now;
	url: string;
}

export interface UrlReadyLater {
	ready: Ready.later;
}

export type UrlReadyWhen = UrlReadyNow | UrlReadyLater;

/**
 * A common pattern when downloading a document is to first resolve that document's URL, which may
 * be known now (e.g. if the document was already cached) or will be sent later via an
 * asynchronous notification (e.g. once the document becomes cached). This function encapsulates
 * that process into a simple Promise-based API.
 *
 * @param urlReadyWhenPromise A promised request for the document URL.
 * @param messageType The {@link DocumentStatusMessageType} to watch for.
 * @param documentId The ID of the document to watch for.
 */
export const resolveDownloadUrl = async (
	urlReadyWhenPromise: Promise<UrlReadyWhen> | UrlReadyWhen,
	messageType: DocumentStatusMessageType,
	documentId: string,
): Promise<string> => {
	const urlReadyWhen: UrlReadyWhen = await urlReadyWhenPromise;

	switch (urlReadyWhen.ready) {
		case Ready.now:
			return urlReadyWhen.url;

		case Ready.later:
			return new Promise((resolve, reject): void => {
				const listener: NotifyListener = (event: NotifyEvent): void => {
					if (
						event.message.type === messageType &&
						event.message.document_id === documentId
					) {
						AsyncNotifications.singleton.removeEventListener(notify, listener);

						if (event.message.ready) {
							resolve(event.message.fname);
						} else {
							reject(event.message.msg);
						}
					}
				};
				AsyncNotifications.singleton.addEventListener(notify, listener);
			});

		default:
			throw new TypeError(
				`Invalid value for type 'Ready': '${(urlReadyWhen as UrlReadyWhen).ready}'`,
			);
	}
};
